<script setup lang="ts">
/**
 * CreateBioModal — 1:1 порт docs/mockups/modal-create-bio.html (inbox-179).
 *
 * Спека: docs/specs/2026-05-19-pages-behavior.md §7 «Bio-страницы — список».
 * Primary CTA «+ Создать» → modal:create-bio (slug + template chooser) ·
 * success → redirect `/bio/:id/edit` (bio-constructor).
 *
 * Секции мокапа:
 *   1. Slug input с префиксом «link.helmix.app/@» + inline status
 *      (Свободно / Занято / мин. 3 символа). Pattern [a-z0-9_-]+, длина 3–32.
 *   2. Шаблон — 5 radio-cards: Minimal / Modern / Creative / Business /
 *      Пустой. Каждая с phone-mock превью внутри (color-blocks под layout
 *      шаблона). Checked-обводка + ✓-dot top-right.
 *
 * BioApi.create({ slug, theme_json: { template }, status: 'draft' }) →
 * router.push('/bio/' + page.id + '/edit') → modals.close().
 *
 * Slug availability — клиент-валидация (pattern + reserved) + 409 от
 * сервера. Backend dedicated check-endpoint'а пока нет.
 *
 * Открытие из BioListView:
 *   modals.open('create-bio');
 *   modals.on('create-bio', (page) => { … });
 */

import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { BioApi, ApiError, type BioPage, type BioPageCreate } from '@/api/bio';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiModalFooter from '@/components/ui/UiModalFooter.vue';

type Template = 'minimal' | 'modern' | 'creative' | 'business' | 'empty';

interface TemplateOption {
  value: Template;
  name: string;
  desc: string;
}
const TEMPLATES: TemplateOption[] = [
  { value: 'minimal', name: 'Minimal', desc: 'Светло, тихо, без отвлечений.' },
  { value: 'modern', name: 'Modern', desc: 'Hero-обложка и социальные чипы.' },
  { value: 'creative', name: 'Creative', desc: 'Сетка тайлов под медиа и галерею.' },
  { value: 'business', name: 'Business', desc: 'Карточка с лого, контактами, CTA.' },
  { value: 'empty', name: 'Пустой', desc: 'Собрать самостоятельно в конструкторе.' },
];

// Зарезервированные слаги — нельзя занимать через bio-page, server вернёт
// 409, но проверка на клиенте даёт мгновенный feedback.
const RESERVED_SLUGS = new Set([
  'admin', 'support', 'help', 'api', 'auth', 'login', 'signup',
  'recover', 'dashboard', 'links', 'campaigns', 'bio', 'qr',
  'analytics', 'settings', 'team', 'billing', 'profile', 'org',
  'domains', 'webhooks', 'audit', 'me', 'test', 'staff', 'about',
  'pricing', 'features', 'integrations', 'docs', 'cases', 'ai',
]);
const SLUG_PATTERN = /^[a-z0-9_-]+$/;

const router = useRouter();
const modals = useModalStore();

const slug = ref('');
const template = ref<Template>('minimal');
const error = ref<string | null>(null);
const submitting = ref(false);

watch(
  () => modals.activeKey,
  (key) => {
    if (key !== 'create-bio') return;
    slug.value = '';
    template.value = 'minimal';
    error.value = null;
    submitting.value = false;
  },
);

interface SlugStatus {
  level: 'ok' | 'busy' | 'checking' | 'idle';
  text: string;
}
const slugStatus = computed<SlugStatus>(() => {
  const v = slug.value.toLowerCase().trim();
  if (!v) return { level: 'idle', text: '—' };
  if (v.length < 3) return { level: 'busy', text: 'мин. 3 символа' };
  if (v.length > 32) return { level: 'busy', text: 'макс. 32 символа' };
  if (!SLUG_PATTERN.test(v)) return { level: 'busy', text: 'только a-z, 0-9, _, -' };
  if (RESERVED_SLUGS.has(v)) return { level: 'busy', text: 'зарезервировано' };
  return { level: 'ok', text: 'Свободно' };
});

const canSubmit = computed(
  () => slugStatus.value.level === 'ok' && !submitting.value,
);

async function submit(): Promise<void> {
  if (!canSubmit.value) {
    if (slugStatus.value.level === 'busy') {
      error.value = slugStatus.value.text;
    } else if (slugStatus.value.level === 'idle') {
      error.value = 'Введите адрес страницы';
    }
    return;
  }
  error.value = null;
  submitting.value = true;
  modals.setPending(true);
  try {
    const body: BioPageCreate = {
      slug: slug.value.toLowerCase().trim(),
      theme_json: { template: template.value },
      status: 'draft',
    };
    const page: BioPage = await BioApi.create(body);
    modals.emit('create-bio', page);
    modals.close();
    void router.push(`/bio/${page.id}/edit`);
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) error.value = 'Сессия истекла. Перезайдите.';
      else if (e.status === 403) error.value = 'Недостаточно прав для создания bio-страницы.';
      else if (e.status === 409) error.value = 'Этот адрес уже занят. Попробуйте другой.';
      else if (e.status === 422) error.value = e.message || 'Невалидные данные.';
      else if (e.status === 429) error.value = 'Слишком много запросов. Подождите минуту.';
      else error.value = e.message || `Ошибка сервера (${e.status})`;
    } else {
      error.value = (e as Error).message || 'Сеть недоступна.';
    }
  } finally {
    submitting.value = false;
    modals.setPending(false);
  }
}

function cancel(): void {
  if (submitting.value) return;
  modals.close();
}
</script>

<template>
  <UiModal
    title="Создать bio-страницу"
    subtitle="Лендинг-визитка по короткой ссылке. Можно изменить шаблон позднее в конструкторе."
    size="md"
    :disable-close="submitting"
    @close="cancel"
  >
    <form
      id="create-bio-form"
      @submit.prevent="submit"
    >
      <!-- Slug input -->
      <div class="field">
        <label
          class="field-label"
          for="bio-slug"
        >Адрес страницы</label>
        <div
          class="slug-input"
          :class="`status-${slugStatus.level}`"
        >
          <span class="slug-prefix">hopw.cc/@</span>
          <input
            id="bio-slug"
            v-model="slug"
            type="text"
            placeholder="ivanov"
            minlength="3"
            maxlength="32"
            pattern="[a-z0-9_\-]+"
            autocomplete="off"
            :disabled="submitting"
            required
          >
          <span
            class="slug-status"
            :class="slugStatus.level"
            aria-live="polite"
          >
            <svg
              v-if="slugStatus.level === 'ok'"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <svg
              v-else-if="slugStatus.level === 'busy'"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              aria-hidden="true"
            >
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
            {{ slugStatus.text }}
          </span>
        </div>
        <small class="field-hint">
          Только латиница, цифры, дефис и подчёркивание. От 3 до 32 символов.
          Можно изменить только в первые 7 дней.
        </small>
      </div>

      <!-- Templates -->
      <div class="field">
        <span class="field-label">Шаблон</span>
        <div
          class="tpl-grid"
          role="radiogroup"
          aria-label="Шаблон"
        >
          <label
            v-for="t in TEMPLATES"
            :key="t.value"
            :class="['tpl-card', { selected: template === t.value }]"
          >
            <input
              v-model="template"
              type="radio"
              name="bio-template"
              :value="t.value"
              :disabled="submitting"
            >
            <div class="phone">
              <div :class="['phone-screen', `tpl-${t.value}`]">
                <!-- Minimal -->
                <template v-if="t.value === 'minimal'">
                  <div class="ph-avatar" />
                  <div class="ph-name" />
                  <div class="ph-sub" />
                  <div class="ph-link" />
                  <div class="ph-link" />
                  <div class="ph-link" />
                </template>
                <!-- Modern -->
                <template v-else-if="t.value === 'modern'">
                  <div class="ph-hero" />
                  <div class="ph-avatar" />
                  <div class="ph-name" />
                  <div class="ph-row">
                    <div class="ph-chip" />
                    <div class="ph-chip" />
                    <div class="ph-chip" />
                  </div>
                  <div class="ph-link" />
                  <div class="ph-link" />
                </template>
                <!-- Creative -->
                <template v-else-if="t.value === 'creative'">
                  <div class="ph-avatar" />
                  <div class="ph-name" />
                  <div class="ph-sub" />
                  <div class="ph-grid">
                    <div class="ph-tile" />
                    <div class="ph-tile" />
                    <div class="ph-tile" />
                    <div class="ph-tile" />
                  </div>
                </template>
                <!-- Business -->
                <template v-else-if="t.value === 'business'">
                  <div class="ph-row top">
                    <div class="ph-logo" />
                    <div class="ph-text">
                      <div class="ph-name" />
                      <div class="ph-sub" />
                    </div>
                  </div>
                  <div class="ph-card" />
                  <div class="ph-link primary" />
                  <div class="ph-link" />
                  <div class="ph-row tight">
                    <div class="ph-icon" />
                    <div class="ph-icon" />
                    <div class="ph-icon" />
                    <div class="ph-icon" />
                  </div>
                </template>
                <!-- Empty -->
                <template v-else>
                  <div class="empty-mark">+</div>
                  <span class="empty-label">пустой холст</span>
                </template>
              </div>
            </div>
            <div class="tpl-meta">
              <span class="tpl-name">{{ t.name }}</span>
              <span class="tpl-desc">{{ t.desc }}</span>
            </div>
          </label>
        </div>
      </div>

      <p
        v-if="error"
        class="form-msg err"
        role="alert"
      >
        {{ error }}
      </p>
    </form>

    <template #footer>
      <UiModalFooter
        save-type="submit"
        save-form="create-bio-form"
        :save-disabled="!canSubmit"
        :save-loading="submitting"
        :cancel-disabled="submitting"
        @cancel="cancel"
      >
        <template #save>Создать и открыть конструктор</template>
      </UiModalFooter>
    </template>
  </UiModal>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
}
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--fg);
  display: block;
}
.field-hint {
  font-size: 12px;
  color: var(--fg-muted);
  line-height: 1.4;
}

/* Slug input */
.slug-input {
  display: flex;
  align-items: stretch;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.12s;
}
.slug-input:focus-within {
  border-color: var(--accent);
}
.slug-input.status-busy {
  border-color: var(--danger);
}
.slug-prefix {
  padding: 8px 4px 8px 12px;
  background: var(--bg);
  color: var(--fg-muted);
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 13.5px;
  display: inline-flex;
  align-items: center;
  border-right: 1px solid var(--border);
}
.slug-input input {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: 0;
  outline: 0;
  padding: 8px 10px;
  color: var(--fg);
  font-family: 'SF Mono', Menlo, monospace;
  font-size: 13.5px;
}
.slug-input input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.slug-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}
.slug-status.ok {
  color: var(--success);
}
.slug-status.busy {
  color: var(--danger);
}
.slug-status.checking,
.slug-status.idle {
  color: var(--fg-dim);
}

/* Templates */
.tpl-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}
.tpl-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
  position: relative;
}
.tpl-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.tpl-card:hover {
  border-color: var(--accent);
}
.tpl-card.selected {
  border-color: var(--accent);
  background: var(--accent-soft);
}
.tpl-card.selected::after {
  content: '';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
  background: var(--accent);
  border-radius: 50%;
  box-shadow: 0 0 0 3px var(--bg-card), inset 0 0 0 3px var(--accent-soft);
}
.tpl-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 2px;
}
.tpl-name {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fg);
}
.tpl-desc {
  font-size: 11px;
  color: var(--fg-muted);
  line-height: 1.35;
}

/* Phone frame */
.phone {
  background: #1a1f29;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 4px;
  aspect-ratio: 9 / 16;
  overflow: hidden;
}
.phone-screen {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow: hidden;
  position: relative;
}

/* Minimal preview */
.tpl-minimal {
  background: #f6f7f9;
}
.tpl-minimal .ph-avatar {
  width: 22px;
  height: 22px;
  background: #c5cdd6;
  border-radius: 50%;
  margin: 4px auto 2px;
}
.tpl-minimal .ph-name {
  height: 6px;
  width: 50%;
  background: #2a3340;
  border-radius: 2px;
  margin: 2px auto;
}
.tpl-minimal .ph-sub {
  height: 4px;
  width: 36%;
  background: #9aa5b1;
  border-radius: 2px;
  margin: 0 auto 4px;
}
.tpl-minimal .ph-link {
  height: 8px;
  background: #fff;
  border: 1px solid #d5dae1;
  border-radius: 4px;
}

/* Modern preview */
.tpl-modern {
  background: #0f1620;
  padding: 0;
}
.tpl-modern .ph-hero {
  height: 22px;
  background: linear-gradient(135deg, #4f8cff, #b366ff);
  margin-bottom: -8px;
}
.tpl-modern .ph-avatar {
  width: 18px;
  height: 18px;
  background: #fff;
  border: 2px solid #0f1620;
  border-radius: 50%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}
.tpl-modern .ph-name {
  height: 5px;
  width: 40%;
  background: #fff;
  border-radius: 2px;
  margin: 4px auto 2px;
}
.tpl-modern .ph-row {
  display: flex;
  gap: 3px;
  justify-content: center;
  margin-top: 2px;
}
.tpl-modern .ph-chip {
  width: 12px;
  height: 6px;
  background: rgba(79, 140, 255, 0.4);
  border-radius: 100px;
}
.tpl-modern .ph-link {
  height: 7px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin: 2px 6px;
}

/* Creative preview */
.tpl-creative {
  background: linear-gradient(160deg, #2a1a4a, #1a1f3a);
}
.tpl-creative .ph-avatar {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #ff5d8f, #ff8b4d);
  border-radius: 8px;
  transform: rotate(-6deg);
  margin: 4px 0 4px 6px;
}
.tpl-creative .ph-name {
  height: 7px;
  width: 60%;
  background: #fff;
  border-radius: 2px;
  margin: 2px 6px;
}
.tpl-creative .ph-sub {
  height: 4px;
  width: 40%;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 2px;
  margin: 0 6px 6px;
}
.tpl-creative .ph-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;
  padding: 0 4px;
}
.tpl-creative .ph-tile {
  aspect-ratio: 1;
  background: rgba(255, 93, 143, 0.3);
  border-radius: 4px;
}
.tpl-creative .ph-tile:nth-child(2) {
  background: rgba(255, 139, 77, 0.3);
}
.tpl-creative .ph-tile:nth-child(3) {
  background: rgba(179, 102, 255, 0.3);
}
.tpl-creative .ph-tile:nth-child(4) {
  background: rgba(92, 215, 225, 0.3);
}

/* Business preview */
.tpl-business {
  background: #fff;
  color: #2a3340;
}
.tpl-business .ph-row.top {
  display: flex;
  gap: 4px;
  align-items: center;
}
.tpl-business .ph-logo {
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #4f8cff, #b366ff);
  border-radius: 3px;
  flex-shrink: 0;
}
.tpl-business .ph-text {
  flex: 1;
}
.tpl-business .ph-name {
  height: 5px;
  width: 80%;
  background: #2a3340;
  border-radius: 1px;
  margin-bottom: 2px;
}
.tpl-business .ph-sub {
  height: 3px;
  width: 60%;
  background: #9aa5b1;
  border-radius: 1px;
}
.tpl-business .ph-card {
  height: 18px;
  background: linear-gradient(135deg, rgba(79, 140, 255, 0.12), rgba(79, 140, 255, 0.04));
  border: 1px solid rgba(79, 140, 255, 0.2);
  border-radius: 4px;
}
.tpl-business .ph-link {
  height: 8px;
  background: #f0f3f7;
  border-radius: 4px;
}
.tpl-business .ph-link.primary {
  background: #4f8cff;
}
.tpl-business .ph-row.tight {
  display: flex;
  gap: 3px;
  justify-content: center;
  margin-top: auto;
  padding-top: 4px;
}
.tpl-business .ph-icon {
  width: 8px;
  height: 8px;
  background: #e6eaf0;
  border-radius: 50%;
}

/* Empty preview */
.tpl-empty {
  background: var(--bg);
  border: 2px dashed var(--border-strong);
  display: grid;
  place-items: center;
  color: var(--fg-dim);
}
.tpl-empty .empty-mark {
  font-size: 22px;
  line-height: 1;
}
.tpl-empty .empty-label {
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 2px;
}

/* Form message */
.form-msg {
  padding: 8px 12px;
  font-size: 12.5px;
  border-radius: 6px;
  margin-top: 8px;
}
.form-msg.err {
  color: var(--danger);
  background: var(--danger-soft);
}

@media (max-width: 980px) {
  .tpl-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 640px) {
  .tpl-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .slug-status {
    padding: 0 10px;
    font-size: 11.5px;
  }
}
</style>
